import { useMyMutation, MutationInfos, client, Options } from '../helpers';
import {
    CreateVoucherInput,
    DeletePaymentCardInput,
    DeleteVoucherInput,
    DropinInput,
    EditPaymentCardInput,
    SetDefaultPaymentCardInput,
    UpdatePaymentGroupPolicyInput,
    UpdateVoucherInput,
} from '../../apiTypes';
import { stringsRes } from '../../../resources/strings';
import { ToastWithError, ToastWithIcon } from '../../../navigation/routes/toastProvider';
import { typePayments } from './types';

// Dropin Adyen
function callServer(variables: DropinInput): MutationInfos {
    const { url, data } = variables;
    return {
        method: 'post',
        path: url,
        type: typePayments.CALL_SERVER,
        params: {
            ...data,
            integration: 'dropin',
        },
    };
}

// Mutation function
function setDefaultCard(variables: SetDefaultPaymentCardInput): MutationInfos {
    return {
        method: 'put',
        path: `/payment-methods/cards/${variables?.id}/main-card`,
        type: typePayments.SET_DEFAULT_CARD,
        params: {
            ...variables,
        },
    };
}

function updateCard(variables: EditPaymentCardInput): MutationInfos {
    return {
        method: 'put',
        path: `/payment-methods/cards/${variables?.id}`,
        type: typePayments.UPDATE_CARD,
        params: {
            ...variables,
        },
    };
}

function updateGroupPaymentPolicy(variables: UpdatePaymentGroupPolicyInput): MutationInfos {
    return {
        method: 'put',
        path: `/payment-methods/settings`,
        type: typePayments.UPDATE_CARD,
        params: {
            ...variables,
        },
    };
}

function deleteCard(variables: DeletePaymentCardInput): MutationInfos {
    return {
        method: 'delete',
        path: `/payment-methods/cards/${variables?.id}`,
        type: typePayments.DELETE_CARD,
        params: {
            ...variables,
        },
    };
}

function createVoucher(variables: CreateVoucherInput): MutationInfos {
    return {
        method: 'post',
        path: '/payment-methods/vouchers',
        type: typePayments.CREATE_VOUCHER,
        params: {
            ...variables,
        },
    };
}

function updateVoucher(variables: UpdateVoucherInput): MutationInfos {
    const { id, ...voucher } = variables;
    return {
        method: 'put',
        path: `/payment-methods/vouchers/${id}`,
        type: typePayments.UPDATE_VOUCHER,
        params: {
            ...voucher,
        },
    };
}

function deleteVoucher(variables: DeleteVoucherInput): MutationInfos {
    return {
        method: 'delete',
        // contract_id is expected as a query param, and useMyMutation sends `params` as a body on delete
        path: `/payment-methods/vouchers/${variables?.id}?contract_id=${variables?.contract_id}`,
        type: typePayments.DELETE_VOUCHER,
        params: {},
    };
}

export function useSetDefaultCard() {
    return useMyMutation(setDefaultCard, {
        onSuccess: () => {
            // client.invalidateQueries('cards');
            ToastWithIcon('info', stringsRes.common.saved);
        },
        onError: () => {
            ToastWithIcon('error', stringsRes.payment.errors.defaultCardUpdate);
        },
    });
}

export function useUpdateCard() {
    return useMyMutation(updateCard, {
        onSuccess: () => {
            client.invalidateQueries('cards');
            client.invalidateQueries('group-settings');
            ToastWithIcon('info', stringsRes.common.edited);
        },
    });
}

export function useUpdateSettings(opts: Options<UpdatePaymentGroupPolicyInput> = {}) {
    return useMyMutation(updateGroupPaymentPolicy, {
        onSuccess: () => {
            client.invalidateQueries('group-settings');
            ToastWithIcon('info', stringsRes.common.edited);
        },
        ...opts,
    });
}

export function useDeleteCard() {
    return useMyMutation(deleteCard, {
        onSuccess: () => {
            client.invalidateQueries('cards');
            client.invalidateQueries('group-settings');
            ToastWithIcon('info', stringsRes.common.deleted);
        },
        // onError: () => {
        //     ToastWithIcon('error', stringsRes.payment.errors.cardDeletion);
        // },
    });
}

// Vouchers are picked in the group payment policies, so those have to be refetched too.
const invalidateVouchers = () => {
    client.invalidateQueries('vouchers');
    client.invalidateQueries('group-settings');
};

export function useCreateVoucher(opts: Options<CreateVoucherInput> = {}) {
    return useMyMutation(createVoucher, {
        onSuccess: () => {
            invalidateVouchers();
            ToastWithIcon('info', stringsRes.common.added);
        },
        ...opts,
    });
}

export function useUpdateVoucher(opts: Options<UpdateVoucherInput> = {}) {
    return useMyMutation(updateVoucher, {
        onSuccess: () => {
            invalidateVouchers();
            ToastWithIcon('info', stringsRes.common.edited);
        },
        ...opts,
    });
}

export function useDeleteVoucher(opts: Options<DeleteVoucherInput> = {}) {
    return useMyMutation(deleteVoucher, {
        onSuccess: () => {
            invalidateVouchers();
            ToastWithIcon('info', stringsRes.common.deleted);
        },
        onError: (error: Error) => {
            // 422 VoucherInUse: the coverage is still applied by the payment policy of a group.
            // The API answers `errorName`, which ToastWithError (built around `errorCode`) can't read.
            if ((error as any)?.response?.data?.errorName === 'VoucherInUse') {
                ToastWithIcon('error', stringsRes.admin.payment.voucher.errors.inUse);
                return;
            }
            ToastWithError(error);
        },
        ...opts,
    });
}

export function useCallDropin() {
    return useMyMutation(callServer, {
        onSuccess: () => {},
    });
}
